/**
 * <help-quiz> — stomer-quiz (templates/page.help-mij-kiezen.liquid).
 *
 * Panels (.quiz-panel): [data-step="q1|q2a|q2b"] (q1 visible on load) and [data-result-panel="model"]; one is visible at a time.
 * Answers: <button data-q data-a data-next="step"> or <button data-result="model[,addon]">.
 * Controls: [data-quiz-back], [data-quiz-restart]. Live region: [data-quiz-live].
 * Analytics: custom events via Shopify.analytics.publish (quiz_start, quiz_answer, quiz_result,
 * quiz_cta_click, quiz_doubt_click, quiz_color, quiz_restart, quiz_back), readable in a custom pixel.
 */
class HelpQuiz extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll(".quiz-panel")];
    this.live = this.querySelector("[data-quiz-live]");
    this.history = [];
    this.current = "q1";
    this.started = false;
    this.result = null;

    this.onClick = (event) => {
      const target = event.target;
      const option = target.closest("button[data-next], button[data-result]");
      if (option && this.contains(option)) return this.choose(option);
      const swatch = target.closest("button[data-swatch]");
      if (swatch) return this.pickColor(swatch);
      if (target.closest("[data-quiz-back]")) return this.back();
      if (target.closest("[data-quiz-restart]")) return this.restart();
      const cta = target.closest("a[data-cta]");
      if (cta) this.track("quiz_cta_click", { result: this.result, target: cta.dataset.cta });
      const doubt = target.closest("a[data-doubt]");
      if (doubt) this.track("quiz_doubt_click", { result: this.result, product: doubt.dataset.doubt });
    };
    this.addEventListener("click", this.onClick);
    this.render(false);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }

  track(name, data) {
    try {
      window.Shopify?.analytics?.publish?.(name, { quiz: "stomer-quiz", ...data });
    } catch (error) {
      // Analytics must never block the quiz.
    }
  }

  choose(option) {
    if (this.busy) return;
    option
      .closest(".quiz-panel")
      ?.querySelectorAll("button[aria-pressed]")
      .forEach((button) => button.setAttribute("aria-pressed", String(button === option)));
    // The quiz opens on question 1 (no start button), so the first answer counts as the start.
    if (!this.started) {
      this.started = true;
      this.track("quiz_start", {});
    }
    this.track("quiz_answer", { question: option.dataset.q, answer: option.dataset.a });
    this.busy = true;
    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 300;
    setTimeout(() => {
      this.busy = false;
      if (option.dataset.next) {
        this.go(option.dataset.next);
        return;
      }
      const [model, flag] = option.dataset.result.split(",");
      this.result = model;
      const addon = this.querySelector(`[data-result-panel="${model}"] [data-addon]`);
      if (addon) addon.hidden = flag !== "addon";
      this.track("quiz_result", { product: model, via: `${option.dataset.q}${option.dataset.a}` });
      this.go(`result:${model}`);
    }, delay);
  }

  /** Colour swatches in a result (Professional: Zwart/Zand): swap the photo and point the product links to that colour. */
  pickColor(swatch) {
    const panel = swatch.closest("[data-result-panel]");
    if (!panel) return;
    const choice = swatch.dataset.swatch;
    panel.querySelectorAll("button[data-swatch]").forEach((button) => {
      button.setAttribute("aria-checked", String(button === swatch));
    });
    panel.querySelectorAll("[data-swatch-image]").forEach((image) => {
      image.hidden = image.dataset.swatchImage !== choice;
    });
    panel.querySelectorAll(`a[data-cta="${panel.dataset.resultPanel}"]`).forEach((link) => {
      link.href = swatch.dataset.url;
    });
    const name = panel.querySelector("[data-swatch-name]");
    if (name && swatch.dataset.name) name.textContent = swatch.dataset.name;
    this.track("quiz_color", { result: panel.dataset.resultPanel, color: choice });
  }

  go(next) {
    this.history.push(this.current);
    this.current = next;
    this.render(true);
  }

  back() {
    if (this.history.length === 0) return;
    this.track("quiz_back", { from: this.current });
    this.current = this.history.pop();
    this.render(true);
  }

  restart() {
    this.track("quiz_restart", { result: this.result });
    this.history = [];
    this.result = null;
    this.querySelectorAll("button[aria-pressed]").forEach((button) => button.setAttribute("aria-pressed", "false"));
    this.querySelectorAll("details[open]").forEach((details) => details.removeAttribute("open"));
    this.querySelectorAll('button[data-swatch="default"]').forEach((swatch) => this.pickColor(swatch));
    this.current = "q1";
    this.render(true);
  }

  panelFor(state) {
    if (state.startsWith("result:")) return this.querySelector(`[data-result-panel="${state.slice(7)}"]`);
    return this.querySelector(`[data-step="${state}"]`);
  }

  render(moveFocus) {
    const visible = this.panelFor(this.current);
    this.panels.forEach((panel) => {
      panel.hidden = panel !== visible;
    });
    if (!moveFocus || !visible) return;

    const heading = visible.querySelector("h2");
    const progress = visible.querySelector("[data-progress]");
    if (this.live) {
      const parts = [progress?.textContent, heading?.textContent].filter(Boolean).map((text) => text.trim());
      this.live.textContent = parts.join(". ");
    }
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    } else {
      visible.querySelector("button")?.focus({ preventScroll: true });
    }
    const top = this.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.4) this.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

if (!customElements.get("help-quiz")) {
  customElements.define("help-quiz", HelpQuiz);
}
